import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import type { AgentPage, FlowValidationProblem } from '../../types/negotiationAgents';
import type {
  AgentSettingMutation,
  AgentSettingMutationResult,
  AgentSettingQuery,
  AgentSettingResource,
  AgentSettingDocument,
  AgentSettingDocumentRequest,
} from '../../types/agentSettings';
import { agentQueryKey } from '../../utils/negotiationAgents/queryKey';
import { toAgentProblem } from '../../utils/negotiationAgents/apiResponse';
import { agentSettingDocumentError } from '../../utils/negotiationAgents/agentSettingDocumentValidation';
import { fetchNegotiationAgent } from '../negotiationAgents/actions';
import { agentSettingsService } from './services';

export const fetchAgentSettingsPage = createAsyncThunk<
  { key: string; page: AgentPage<AgentSettingResource> },
  AgentSettingQuery,
  {
    state: Pick<RootState, 'agentSettings' | 'negotiationAgents'>;
    rejectValue: FlowValidationProblem;
  }
>(
  'agentSettings/page',
  async (query, { rejectWithValue }) => {
    try {
      return { key: agentQueryKey(query), page: await agentSettingsService.page(query) };
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  {
    condition: (query, { getState }) =>
      getState().agentSettings.pages[agentQueryKey(query)]?.status !== 'loading',
  },
);

export const mutateAgentSetting = createAsyncThunk<
  AgentSettingMutationResult,
  AgentSettingMutation,
  {
    state: Pick<RootState, 'agentSettings' | 'negotiationAgents'>;
    rejectValue: FlowValidationProblem;
  }
>(
  'agentSettings/mutate',
  async (input, { dispatch, rejectWithValue }) => {
    try {
      const result = await agentSettingsService.mutate(input);
      await dispatch(fetchNegotiationAgent({ id: input.agentId, refresh: true }));
      return result;
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  {
    condition: (input, { getState }) =>
      getState().agentSettings.mutations[input.agentId]?.status !== 'loading',
  },
);

export const requestAgentSettingDocument = createAsyncThunk<
  AgentSettingDocument,
  AgentSettingDocumentRequest,
  { state: Pick<RootState, 'agentSettings'>; rejectValue: FlowValidationProblem }
>(
  'agentSettings/document',
  async (input, { rejectWithValue }) => {
    const problem = input.file ? agentSettingDocumentError(input.file) : null;
    if (problem) return rejectWithValue(problem);
    try {
      return await agentSettingsService.document(input);
    } catch (error) {
      return rejectWithValue(toAgentProblem(error));
    }
  },
  {
    condition: (input, { getState }) =>
      getState().agentSettings.documents[input.requestKey]?.status !== 'loading',
  },
);
